<template>
  <button
@click="signInWithGoogle" :disabled="loading"
    class="w-full flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
    :class="{ 'opacity-50 cursor-not-allowed': loading }"
>
    <svg
v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg"
      fill="none" viewBox="0 0 24 24"
>
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path
class="opacity-75" fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
>
      </path>
    </svg>

    <svg v-else class="w-5 h-5 mr-3" viewBox="0 0 24 24">
      <path
fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
/>
      <path
fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
/>
      <path
fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
/>
      <path
fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
/>
    </svg>

    <span>{{ loading ? 'Signing in...' : 'Continue with Google' }}</span>
  </button>
</template>

<script>
import { Auth, Hub } from 'aws-amplify';

export default {
  name: 'GoogleSignInButton',
  data() {
    return {
      loading: false
    };
  },
  mounted() {
    // Listen for OAuth callback events only for this component
    this.hubListener = Hub.listen('auth', async ({ payload: { event, data } }) => {
      switch (event) {
        case 'cognitoHostedUI':
          // Wait a moment for the user session to be fully established
          setTimeout(async () => {
            try {
              // Get the current authenticated user to ensure we have the full user object
              const user = await Auth.currentAuthenticatedUser();
              // Set default attributes for new Google users
              await this.ensureUserAttributes();
              // Update the store with user data
              this.$store.dispatch('fetchUser', user);
              // Redirect to dashboard after successful OAuth sign-in
              this.$router.push('/gifts');
            } catch (error) {
              console.error('Error processing OAuth user:', error);
              // Still try to update store and redirect
              this.$store.dispatch('fetchUser', data);
              this.$router.push('/gifts');
            }
          }, 1000);
          break;
        case 'cognitoHostedUI_failure':
          this.$emit('error', 'OAuth authentication failed. Please try again.');
          break;
      }
    });
  },
  beforeUnmount() {
    // Clean up listener when component is destroyed
    if (this.hubListener) {
      this.hubListener();
    }
  },
  methods: {
    async ensureUserAttributes() {
      try {
        // Get the current authenticated user
        const user = await Auth.currentAuthenticatedUser();

        // Get current user attributes
        const userAttributes = await Auth.userAttributes(user);

        const attributeMap = {};
        userAttributes.forEach(attr => {
          attributeMap[attr.Name] = attr.Value;
        });

        // Detect country from URL params or browser locale
        let countryName = 'Australia'; // default

        // Check URL parameters first
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('country')) {
          const countryParam = urlParams.get('country').trim();
          if (countryParam.length === 2) {
            countryName = this.expandCountryCode(countryParam.toUpperCase()) || countryName;
          } else {
            countryName = countryParam;
          }
        } else {
          // Fallback to browser locale
          const locale = navigator.language || navigator.userLanguage;
          if (locale) {
            const countryCode = locale.split('-')[1];
            if (countryCode) {
              countryName = this.expandCountryCode(countryCode.toUpperCase()) || countryName;
            }
          }
        }

        // Define default attributes for new users
        const defaultAttributes = {
          'custom:country': countryName,
          'custom:show_mothers_day': 'true',
          'custom:show_fathers_day': 'true',
          'custom:remind_mothers_day': 'true',
          'custom:remind_fathers_day': 'true',
          'custom:remind_christmas': 'true',
          'custom:remind_easter': 'true',
          'custom:remind_valentines': 'true',
        };

        // Check which attributes are missing and need to be set
        const attributesToSet = {};
        for (const [key, value] of Object.entries(defaultAttributes)) {
          if (!attributeMap[key]) {
            attributesToSet[key] = value;
          }
        }

        // Set missing attributes
        if (Object.keys(attributesToSet).length > 0) {
          await Auth.updateUserAttributes(user, attributesToSet);
        }

      } catch (error) {
        console.error('Error setting user attributes:', error);
        // Don't throw error - this shouldn't block the login process
      }
    },

    expandCountryCode(code) {
      const countryMap = {
        'AU': 'Australia',
        'BR': 'Brazil',
        'CA': 'Canada',
        'FJ': 'Fiji',
        'FR': 'France',
        'IN': 'India',
        'IE': 'Ireland',
        'MX': 'Mexico',
        'NL': 'Netherlands',
        'NZ': 'New Zealand',
        'PH': 'Philippines',
        'SC': 'Scotland',
        'SG': 'Singapore',
        'ZA': 'South Africa',
        'GB': 'UK',
        'US': 'USA',
        'WA': 'Wales'
      };
      return countryMap[code] || null;
    },

    async signInWithGoogle() {
      try {
        this.loading = true;

        // Debug: Log current Auth configuration
        const currentConfig = Auth.configure();

        // Use the cognito hosted UI for Google OAuth
        await Auth.federatedSignIn({ provider: 'Google' });

      } catch (error) {
        console.error('Google sign-in error:', error);

        // Provide more detailed error messages
        let errorMessage = 'Failed to sign in with Google';
        if (error.message) {
          if (error.message.includes('domain')) {
            errorMessage = 'OAuth domain not configured properly. Please check your settings.';
          } else if (error.message.includes('redirect')) {
            errorMessage = 'Redirect URL mismatch. Please check your OAuth configuration.';
          } else {
            errorMessage = error.message;
          }
        } else if (error.code === 'ConfigError') {
          errorMessage = 'Google OAuth is not configured. Please check your settings.';
        } else if (error.code === 'NetworkError') {
          errorMessage = 'Network error. Please check your connection and try again.';
        }

        this.$emit('error', errorMessage);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>